import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CardGiftcard,
  Redeem,
  History,
  Star,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { scratchCards } from '../services/api';
import { useNavigate } from 'react-router-dom';

const ScratchCardRedemptionPage = () => {
  const { user, updateUserPoints } = useAuth();
  const navigate = useNavigate();
  const [scratchCode, setScratchCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userPoints, setUserPoints] = useState(null);
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [userHistory, setUserHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Use refs to avoid dependency issues
  const updateUserPointsRef = useRef(updateUserPoints);
  const fetchingRef = useRef(false);
  const lastFetchedUserIdRef = useRef(null);
  updateUserPointsRef.current = updateUserPoints;

  const fetchUserPoints = useCallback(async () => {
    if (fetchingRef.current) return; // Prevent multiple simultaneous calls
    
    try {
      fetchingRef.current = true;
      const response = await scratchCards.getUserPoints();
      setUserPoints(response.data);
      setRedemptionHistory(response.data.redemptionHistory || []);
      // Update user points in context
      updateUserPointsRef.current(response.data.currentPoints || 0);
    } catch (err) {
      console.error('Failed to fetch user points:', err);
    } finally {
      fetchingRef.current = false;
    }
  }, []); // Empty dependency array since we use refs

  const fetchUserHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await scratchCards.getMyHistory();
      setUserHistory(response.data?.history || []);
    } catch (err) {
      console.error('Failed to fetch user history:', err);
      setUserHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.uid !== lastFetchedUserIdRef.current) {
      lastFetchedUserIdRef.current = user.uid;
      fetchUserPoints();
    }
  }, [user, fetchUserPoints]);

  // Note: Removed automatic redirect on logout
  // Users can access product verification page without login

  const handleRedeem = async (e) => {
    e.preventDefault();
    
    if (!scratchCode.trim()) {
      setError('Please enter a scratch card code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await scratchCards.redeem(scratchCode.trim());
      
      setSuccess(`Congratulations! You've earned ${response.data.pointsEarned} points!`);
      setScratchCode('');
      
      // Update user points in context
      updateUserPoints(response.data.totalPoints);
      
      // Refresh user points
      await fetchUserPoints();
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to redeem scratch card');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (!user) {
    return (
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <CardGiftcard sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Sign In Required
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
            Please sign in to redeem loyalty cards and view your loyalty points.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Scratch Card Redemption
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Enter your scratch card code to redeem points and view your loyalty status.
        </Typography>
      </Box>

      {/* User Points Summary */}
      {userPoints && (
        <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Star sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h5" component="div">
                Your Loyalty Points
              </Typography>
            </Box>
            <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
              {userPoints.currentPoints || 0}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Total points earned from loyalty cards: {user?.redemptionHistory?.reduce((acc, curr) => acc + (curr.points || 0), 0) || 0}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<History />}
                onClick={() => {
                  setShowHistory(true);
                  fetchUserHistory();
                }}
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                View History
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Redemption Form */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Redeem sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h5">
            Redeem Scratch Card
          </Typography>
        </Box>

        <form onSubmit={handleRedeem}>
          <TextField
            fullWidth
            label="Scratch Card Code"
            value={scratchCode}
            onChange={(e) => setScratchCode(e.target.value.toUpperCase())}
            placeholder="Enter the code from your scratch card"
            variant="outlined"
            sx={{ mb: 3 }}
            inputProps={{
              style: { 
                fontFamily: 'monospace',
                fontSize: '16px',
                letterSpacing: '2px'
              }
            }}
          />
          
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Redeem />}
            sx={{ width: '100%', py: 1.5 }}
          >
            {loading ? 'Redeeming...' : 'Redeem Card'}
          </Button>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Instructions */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          How to Redeem
        </Typography>
        <Box component="ol" sx={{ pl: 2 }}>
          <Typography component="li" sx={{ mb: 1 }}>
            Scratch off the silver coating on your card to reveal the code
          </Typography>
          <Typography component="li" sx={{ mb: 1 }}>
            Enter the code exactly as shown (case insensitive)
          </Typography>
          <Typography component="li" sx={{ mb: 1 }}>
            Click "Redeem Card" to add points to your account
          </Typography>
          <Typography component="li">
            View your total points and redemption history above
          </Typography>
        </Box>
      </Paper>

      {/* Redemption History Dialog */}
      <Dialog open={showHistory} onClose={() => setShowHistory(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <History sx={{ mr: 1 }} />
            Points History
          </Box>
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (userHistory || []).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CardGiftcard sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="textSecondary">
                No history found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Points</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(userHistory || []).map((history, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2">
                          {history.date 
                            ? new Date(history.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : 'N/A'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {history.cardCode || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${history.points > 0 ? '+' : ''}${history.points || 0} Points`}
                          color={history.points > 0 ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={history.status === 'Deducted' ? 'Awarded' : (history.status || 'Unknown')}
                          color={history.type === 'redemption' ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistory(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScratchCardRedemptionPage;
